import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action, AnyAction } from "typescript-fsa";

import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { DataSelectors, Events, UiStateSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DocumentServiceFactory, EntityInstancePath, GroupInstance } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";

import {
	isGroupInstance,
	isReadonlyGroupInstance,
	isReadonlyGroupInstanceArray,
	isString,
	isStringArray
} from "../../../utilities/documentHelpers";
import { getPathFromFVManifest } from "../../../utilities/modulfFVManifestHelpers";
import { NACHFORDERUNG_EMAIL_ADRESSE, VORGANGSNUMMER } from "../../../utilities/variables/modulfRuntimeAnnotations";
import {
	MAILBODY,
	MAILTO,
	QUERYCONCAT,
	QUERYSPACE,
	QUERYSTART,
	SUBJECT,
	SUBJECT_TEXT
} from "../../../utilities/variables/sendEmailVariables";

const documentService = new DocumentServiceFactory().getDocumentService();

const logger = LoggerFactory.getLogger("modulf");

export function* sendEmailFromDetached(): SagaGenerator<void> {
	yield* takeLatest((action: AnyAction) => {
		return (
			FormEngineActions.event.match(action) &&
			Events.eventButton.match(action.payload.engineEvent) &&
			["send_email_information_request"].includes(action.payload.engineEvent.payload.name)
		);
	}, handle);
}

type EventAction = Action<FormEngineActions.FormEngineEventActions>;
function* handle(action: EventAction): SagaGenerator<void> {
	const { activityId } = action.payload;

	const engineState = yield* select(FormEngineSelectors.engineState(activityId));

	if (!engineState) {
		return;
	}

	const currentScreenLocation = UiStateSelectors.currentScreenLocation()(engineState);
	const document = DataSelectors.document()(engineState) as GroupInstance;

	const path = currentScreenLocation.path;

	const inlineRepeatData = documentService.getAssignedObject(document, path);

	if (!isGroupInstance(inlineRepeatData)) {
		return;
	}

	const activity = yield* select(ActivitySelectors.activityById(activityId));
	if (!activity) {
		logger.error("Couldn't find activity");
		return;
	}

	// Document Model Pfad zur E-Mail-Adresse holen
	const pathToEmail = yield* call(getPathFromFVManifest, NACHFORDERUNG_EMAIL_ADRESSE, activity);
	const pathToVorgangsnummer = yield* call(getPathFromFVManifest, VORGANGSNUMMER, activity);

	const texts = getTextsFromRepeat(inlineRepeatData);

	yield* call(setSendDate, path, activityId);

	createEmail({ document, pathToEmail, pathToVorgangsnummer, texts });
}

function* setSendDate(path: EntityInstancePath, activityId: string) {
	yield* put(
		FormEngineActions.event({
			activityId,
			engineEvent: Events.valueChange({
				path: path.concat([
					{
						elementName: "informationenNachfordern",
						index: 1
					},
					{
						elementName: "nachforderungsDatum",
						index: 1
					}
				]),
				value: new Date()
			})
		})
	);
}

interface CreateEmailParameters {
	document: GroupInstance;
	pathToEmail: EntityInstancePath;
	pathToVorgangsnummer: EntityInstancePath;
	texts: string[];
}

function createEmail({ document, pathToEmail, pathToVorgangsnummer, texts }: CreateEmailParameters) {
	const emailAddress = documentService.getAssignedObject(document, pathToEmail);
	const vorgangsnummer = documentService.getAssignedObject(document, pathToVorgangsnummer);

	if (!isString(emailAddress) || !isString(vorgangsnummer)) {
		logger.error("An error occurred while trying to create the e-mail");
		return;
	}

	const emailBody = texts.join("\n");

	const mailtoURI = formatMailtoURI(emailAddress, vorgangsnummer, emailBody);

	window.open(mailtoURI);
}

function getTextsFromRepeat(repeatData: GroupInstance): string[] {
	if (!Object.keys(repeatData).includes("informationenNachfordern")) {
		return [];
	}

	const data = repeatData.informationenNachfordern;

	if (!isReadonlyGroupInstance(data) || !Object.keys(data).includes("nachforderungsTexte")) {
		return [];
	}

	const entries = data.nachforderungsTexte;

	if (!isReadonlyGroupInstanceArray(entries)) {
		return [];
	}

	const texts = entries.flatMap(x => x.text ?? []);

	if (!isStringArray(texts)) {
		return [];
	}

	return texts;
}

/**
 * Generiert einen mailto URI aus den Parametern.
 * Reservierte und nicht ASCII-Zeichen werden durch Oktette in %-kodierung ersetzt.
 * @param emailAdresse Empfänger der E-Mail
 * @param vorgangsnummer Vorgangsnummer wird Teil des Betreffs
 * @param emailBody E-Mail Text
 */
export function formatMailtoURI(emailAdresse: string, vorgangsnummer: string, emailBody: string): string {
	// Das kann Sonderfälle mit einem escapetem '@' in der Adresse (Beispiel: "not@"@example.com) nicht behandeln.
	// Das sollte aber schon vorher durch Regex Patterns im Form-Field verhindert worden sein.
	const encodedEmailAdresse = emailAdresse
		.split("@")
		.map(x => encodeURIComponent(x))
		.join("@");
	const mailto = MAILTO + encodedEmailAdresse;
	let subject = QUERYSTART + SUBJECT + SUBJECT_TEXT;
	let body = "";
	if (vorgangsnummer) {
		subject += QUERYSPACE + encodeURIComponent(vorgangsnummer);
	}
	if (emailBody) {
		body += QUERYCONCAT + MAILBODY + encodeURIComponent(emailBody);
	}
	return mailto + subject + body;
}
